/**
 * Pure NPI (National Provider Identifier) validation + per-device persistence
 * for the Patient Priority Index worklist access gate (P6c, extended by the
 * auto-link-NPI feature — see useWorklistAccessGate.ts's `resolveWorklistNpi`).
 *
 * The signed-in provider's entitled NPI(s) now arrive WITH the entitled-IPA
 * response (fetchEntitledIpas.ts's `EntitledIpaEntry.providerNpis`): exactly
 * one is auto-linked and deliberately NEVER persisted here (it's re-derived
 * from the server on every load — storing it would only add staleness, and
 * skipping the write is what keeps one IPA's auto NPI from leaking into a
 * different IPA's worklist). More than one still requires an explicit pick,
 * persisted per-device. Zero preserves the original manual free-entry
 * fallback — the provider enters it once and it's remembered on this device.
 *
 * Storage is keyed by (oid, ipa) — NOT just oid — because an explicit
 * pick/manual entry is only ever meaningful for the IPA it was made under; a
 * different provider signing in on the same device (or the same provider's
 * OTHER IPA) must never inherit it. The pre-existing oid-only key
 * (`member-360:worklist-npi:<oid>`, from before IPA-scoping existed) is kept
 * as a read-only fallback so an already-persisted value from before this
 * change isn't silently dropped.
 *
 * PHI posture note: an NPI is a public identifier (NPPES is a public
 * registry), not member PHI — persisting it to localStorage does not violate
 * the "never persist member/search PHI" invariant documented for search
 * filters elsewhere in this repo.
 */

const STORAGE_PREFIX = "member-360:worklist-npi:";

function storageKey(oid: string, ipa: string): string {
  return `${STORAGE_PREFIX}${oid}:${ipa}`;
}

/** Pre-IPA-scoping key — read-only fallback; never written by this module anymore. */
function legacyStorageKey(oid: string): string {
  return `${STORAGE_PREFIX}${oid}`;
}

/**
 * Validates the standard 10-digit NPI check-digit scheme (CMS/NPPES): prepend
 * the constant "80840" prefix to the 10 digits and Luhn-validate the
 * resulting 15-digit string (checksum mod 10 === 0). Doubles every digit at
 * an ODD distance-from-the-right index (0-indexed from the rightmost digit)
 * — verified by hand against CMS's published example NPI `1234567893`.
 */
export function isValidNpi(value: string): boolean {
  if (!/^\d{10}$/.test(value)) return false;
  const digits = `80840${value}`;
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let d = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0;
}

/**
 * Reads the persisted NPI for `oid` scoped to `ipa`, or `null` if absent /
 * unavailable / `ipa` not yet known. Falls back to the pre-IPA-scoping
 * legacy key (see module doc) when the scoped key has never been written.
 */
export function getStoredNpi(
  oid: string | null | undefined,
  ipa: string | null | undefined,
): string | null {
  if (!oid || !ipa || typeof window === "undefined") return null;
  try {
    const scoped = window.localStorage.getItem(storageKey(oid, ipa));
    if (scoped != null) return scoped;
    return window.localStorage.getItem(legacyStorageKey(oid));
  } catch {
    return null;
  }
}

/** Persists `npi` for `oid` scoped to `ipa`. Best-effort — a storage failure is silent. */
export function setStoredNpi(oid: string, ipa: string, npi: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(oid, ipa), npi);
  } catch {
    /* best-effort */
  }
}

/**
 * Clears the persisted NPI for `oid` scoped to `ipa` ("Change NPI"
 * affordance) — also clears the legacy oid-only key so a cleared value can
 * never be silently resurrected by the fallback read above.
 *
 * KNOWN CROSS-IPA CONSEQUENCE (accepted, deliberate): the legacy key is NOT
 * ipa-scoped, so it is the shared fallback for every IPA under this oid.
 * Clearing under IPA-A therefore also retires the fallback that IPA-B would
 * have inherited, and IPA-B will prompt once more. That is unavoidable without
 * either resurrecting cleared values or mutating storage during a render read:
 * one un-scoped legacy value cannot correctly belong to N IPAs at once. The
 * blast radius is deliberately small — it needs a MULTI-IPA provider who also
 * has a pre-IPA-scoping value AND uses "Change NPI" — and the cost is a single
 * re-entry, self-healing on the next load. Auto-link (a single entitled NPI
 * resolves with nothing stored at all) makes stored values rare going forward,
 * so this only matters inside the migration window.
 */
export function clearStoredNpi(oid: string, ipa: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(storageKey(oid, ipa));
    window.localStorage.removeItem(legacyStorageKey(oid));
  } catch {
    /* best-effort */
  }
}
